import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, Textarea } from '../../components/UI';
import { productsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const emptyForm = {
  name: '',
  description: '',
  isActive: true,
};

const ProductModal = ({ isOpen, onClose, product, onSuccess }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!product;

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        description: product.description || '',
        isActive: product.isActive !== false,
      });
    } else {
      setFormData(emptyForm);
    }
    setErrors({});
  }, [product, isOpen]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name || !formData.name.trim()) {
      newErrors.name = 'Product name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (formData.name.trim().length > 50) {
      newErrors.name = 'Name must be 50 characters or less';
    }
    if (formData.description && formData.description.trim().length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        isActive: formData.isActive,
      };

      if (isEditing) {
        await productsAPI.update(product._id, payload);
        toast.success('Product updated successfully');
      } else {
        await productsAPI.create(payload);
        toast.success('Product created successfully');
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to save product';
      toast.error(message);
      if (error.response?.data?.message?.toLowerCase().includes('name')) {
        setErrors({ name: error.response.data.message });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Product' : 'Add Product'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Input
            label={
              <>
                <span className="font-medium">Product Name<span className="text-red-500">*</span></span>
              </>
            }
            placeholder="e.g. CRM Software, Health Insurance Plan"
            value={formData.name}
            onChange={handleChange('name')}
            error={errors.name}
            maxLength={50}
            autoFocus
          />
          <p className={`text-xs text-right mt-0.5 ${formData.name.length > 50 ? 'text-red-500' : 'text-gray-400'}`}>
            {formData.name.length}/50
          </p>
        </div>

        <div>
          <Textarea
            label="Description"
            placeholder="Short description of this product / service"
            value={formData.description}
            onChange={handleChange('description')}
            rows={4}
            maxLength={500}
            error={errors.description}
          />
          <p className={`text-xs text-right mt-0.5 ${formData.description.length > 500 ? 'text-red-500' : 'text-gray-400'}`}>
            {formData.description.length}/500
          </p>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.isActive}
            onChange={handleChange('isActive')}
            className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm font-semibold text-secondary-700">Active</span>
        </label>

        <div className="flex gap-2 sm:gap-3 justify-end pt-2">
          <Button variant="ghost" size="sm" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" type="submit" loading={isSubmitting}>
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ProductModal;